import React, { useRef, useEffect, useState, useCallback } from 'react';

export default function ProtectedImage({ 
  imageData, 
  className, 
  style,
  alt = "Protected image",
  onLoad,
  onError,
  watermark = null 
}) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState(null);

  const renderImage = useCallback(() => {
    const canvas = canvasRef.current;
    const image = imageRef.current;
    
    if (!canvas || !image || !isLoaded) return;

    const ctx = canvas.getContext('2d');
    
    // Set canvas dimensions to match image
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw the image
    ctx.drawImage(image, 0, 0);
    
    // Add watermark if specified
    if (watermark) {
      ctx.save();
      ctx.globalAlpha = 0.3;
      ctx.font = '20px Arial';
      ctx.fillStyle = 'white';
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 1;
      
      const text = watermark;
      const x = canvas.width - ctx.measureText(text).width - 20;
      const y = canvas.height - 20;
      
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
      ctx.restore();
    }
    
    if (onLoad) onLoad();
  }, [imageData, isLoaded, watermark, onLoad]);

  useEffect(() => {
    if (!imageData) {
      setError('No image data provided');
      return;
    }

    const image = new Image();
    imageRef.current = image;
    
    image.onload = () => {
      setIsLoaded(true);
      setError(null);
    };
    
    image.onerror = () => {
      setError('Failed to load image');
      if (onError) onError(new Error('Failed to load image'));
    };

    // Handle different image data types
    if (typeof imageData === 'string') {
      // URL or base64
      image.src = imageData;
    } else if (imageData instanceof File) {
      // File object
      const reader = new FileReader();
      reader.onload = (e) => {
        image.src = e.target.result;
      };
      reader.readAsDataURL(imageData);
    } else if (imageData instanceof Blob) {
      // Blob object
      const url = URL.createObjectURL(imageData);
      image.src = url;
      
      // Cleanup URL when component unmounts
      return () => URL.revokeObjectURL(url);
    }

    return () => {
      if (imageRef.current) {
        imageRef.current.onload = null;
        imageRef.current.onerror = null;
      }
    };
  }, [imageData, onError]);

  useEffect(() => {
    renderImage();
  }, [renderImage]);

  // Prevent right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent drag
  const handleDragStart = (e) => {
    e.preventDefault();
    return false;
  };

  if (error) {
    return (
      <div className={`protected-image-error ${className || ''}`} style={style}>
        <div className="error-message">
          📷 Image unavailable
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className={`protected-image-loading ${className || ''}`} style={style}>
        <div className="loading-spinner">
          ⏳ Loading...
        </div>
      </div>
    );
  }

  return (
    <canvas
      ref={canvasRef}
      className={`protected-image ${className || ''}`}
      style={style}
      onContextMenu={handleContextMenu}
      onDragStart={handleDragStart}
      alt={alt}
      aria-label={alt}
    />
  );
}
